import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

interface VideoTestimonial {
  id: string;
  startAt: number;
}

const TESTIMONIALS: VideoTestimonial[] = [
  { id: "FpUTDTteMqw", startAt: 5 },
  { id: "VAFCne-CNgs", startAt: 0 },
  { id: "yik4DK_MQWM", startAt: 2 },
  { id: "3-A_wcK3gQk", startAt: 0 },
  { id: "6mbxn5wm9Nc", startAt: 1 },
  { id: "49f0Fa-GnOI", startAt: 0 },
];

/**
 * Carrossel horizontal de depoimentos em vídeo (YouTube).
 *
 * - Apenas 1 iframe ativo por vez (lazy mount, performance).
 * - Scroll-snap com peek lateral.
 * - Paleta Allu adaptada do spec original.
 */
export const ParceirosV2DepoimentosInvestidor = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Track qual card está mais centrado pra atualizar dots e scale
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const cards = el.querySelectorAll<HTMLElement>("[data-card]");
      const center = el.scrollLeft + el.clientWidth / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      cards.forEach((card, i) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(cardCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      setCurrentIndex(closestIdx);
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToIndex = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = el.querySelectorAll<HTMLElement>("[data-card]");
    const target = cards[i];
    if (!target) return;
    el.scrollTo({
      left: target.offsetLeft - el.clientWidth / 2 + target.offsetWidth / 2,
      behavior: "smooth",
    });
  };

  const handlePrev = () =>
    scrollToIndex(Math.max(0, currentIndex - 1));
  const handleNext = () =>
    scrollToIndex(Math.min(TESTIMONIALS.length - 1, currentIndex + 1));

  return (
    <section className="bg-[#F3F0E8]">
      <div className="container-wide py-6 sm:py-10">
        {/* Título */}
        <div className="max-w-3xl mb-4 sm:mb-6 pl-4 border-l-4 border-[#1F3D2E]">
          <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-zinc-500">
            Quem já investe
          </div>
          <h2
            className="mt-2.5 text-lg sm:text-xl md:text-2xl font-extrabold text-[#323F4F] tracking-tight leading-snug"
            style={{
              fontFamily: '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
              letterSpacing: "-0.03em",
            }}
          >
            Veja o que dizem os investidores da Allu.
          </h2>
          <p className="mt-2 text-sm sm:text-base text-zinc-600 leading-relaxed">
            Depoimentos reais de quem já investe na Allu.
          </p>
        </div>

        {/* Carrossel */}
        <div className="relative">
          {/* Setas — só desktop/tablet */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="hidden md:flex absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[#1F3D2E] hover:bg-[#2A4A3A] text-white items-center justify-center shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === TESTIMONIALS.length - 1}
            className="hidden md:flex absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[#1F3D2E] hover:bg-[#2A4A3A] text-white items-center justify-center shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Próximo"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Track scrollável — snap-start pros cards alinharem à esquerda
              (assim começa com 3 cards visíveis em vez de centrar o 1º). */}
          <div
            ref={scrollRef}
            className="no-scrollbar flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth py-2 px-4 sm:px-6 lg:px-12"
          >
            {TESTIMONIALS.map((t, i) => {
              const isActive = activeIndex === i;
              const isFocused = currentIndex === i;
              return (
                <div
                  key={t.id}
                  data-card
                  className={`relative flex-shrink-0 w-[calc(100vw-2rem)] sm:w-[380px] aspect-video rounded-2xl overflow-hidden bg-[#1A1A1A] snap-start cursor-pointer transition-all duration-300 ${
                    isFocused
                      ? "scale-105 shadow-[0_12px_40px_rgba(0,0,0,0.25)] opacity-100"
                      : "scale-100 opacity-85"
                  }`}
                  onClick={() => !isActive && setActiveIndex(i)}
                >
                  {isActive ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${t.id}?autoplay=1&rel=0&modestbranding=1&start=${t.startAt}`}
                      className="w-full h-full border-0"
                      title={`Depoimento ${i + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={`https://img.youtube.com/vi/${t.id}/hqdefault.jpg`}
                        alt={`Depoimento ${i + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div
                        className="absolute inset-0 bg-black/30"
                        aria-hidden="true"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                          style={{
                            backgroundColor: "rgba(31, 61, 46, 0.9)",
                          }}
                        >
                          <Play
                            className="w-5 h-5 text-white fill-white ml-0.5"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollToIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                currentIndex === i ? "w-5 bg-[#1F3D2E]" : "w-2 bg-zinc-300"
              }`}
              aria-label={`Ir para depoimento ${i + 1}`}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-6 flex justify-center">
          <a
            href="#form"
            className="inline-flex items-center gap-2 rounded-full font-semibold text-sm h-12 px-7 bg-[#B8934A] hover:bg-[#a37f3f] text-[#1F3D2E] transition-all hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#B8934A]"
          >
            Reservar minha vaga
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  );
};
